import React, { useState, useEffect } from 'react'
import { BindingAdminService, type ProcedureBindingAdmin } from '../../../services/bindingAdminService'
import { getCurrentUserId } from '../../../services/api'

interface BindingsListPageProps {
  onNavigate: (page: string, data?: any) => void
}

export function BindingsListPage({ onNavigate }: BindingsListPageProps) {
  const [bindings, setBindings] = useState<ProcedureBindingAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // フィルタ
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [procedureTypeFilter, setProcedureTypeFilter] = useState('')
  const [sortBy, setSortBy] = useState('updatedAt_desc')

  // ページング
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  useEffect(() => {
    loadBindings()
  }, [page, keyword, statusFilter, procedureTypeFilter, sortBy])

  const loadBindings = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await BindingAdminService.getBindings({
        q: keyword || undefined,
        status: statusFilter || undefined,
        procedureTypeCode: procedureTypeFilter || undefined,
        page,
        size: 20,
        sort: sortBy.replace('_', ',')
      })
      setBindings(result.content)
      setTotalPages(result.totalPages)
      setTotalElements(result.totalElements)
    } catch (err) {
      console.error('Failed to load bindings:', err)
      setError('バインド一覧の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicate = async (bindingId: string) => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを複製しますか？')) return

    try {
      const result = await BindingAdminService.duplicateBinding(bindingId, currentUserId)
      alert('バインドを複製しました')
      onNavigate('binding-detail', { bindingId: result.bindingId })
    } catch (err) {
      console.error('Duplicate failed:', err)
      setError('複製に失敗しました')
    }
  }

  const handleActivate = async (bindingId: string) => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを有効化しますか？\n同じ申請種別の既存有効バインドは自動的に無効化されます。')) return

    try {
      await BindingAdminService.activateBinding(bindingId, currentUserId)
      alert('バインドを有効化しました')
      loadBindings()
    } catch (err: any) {
      console.error('Activate failed:', err)
      setError(err.response?.data?.message || '有効化に失敗しました')
    }
  }

  const handleDeactivate = async (bindingId: string) => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを無効化しますか？')) return

    try {
      await BindingAdminService.deactivateBinding(bindingId, currentUserId)
      alert('バインドを無効化しました')
      loadBindings()
    } catch (err: any) {
      console.error('Deactivate failed:', err)
      setError(err.response?.data?.message || '無効化に失敗しました')
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; label: string }> = {
      ACTIVE: { bg: '#dcfce7', text: '#166534', label: '有効' },
      DRAFT: { bg: '#f3f4f6', text: '#374151', label: '下書き' },
      INACTIVE: { bg: '#fef3c7', text: '#92400e', label: '無効' },
      ARCHIVED: { bg: '#fee2e2', text: '#991b1b', label: '廃止' }
    }
    const config = styles[status] || styles.DRAFT
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        borderRadius: '0.375rem',
        backgroundColor: config.bg,
        color: config.text
      }}>
        {config.label}
      </span>
    )
  }

  const SORT_OPTIONS = [
    { value: 'updatedAt_desc', label: '更新日時（新しい順）' },
    { value: 'updatedAt_asc', label: '更新日時（古い順）' },
    { value: 'procedureTypeCode_asc', label: '申請種別コード（A-Z）' },
    { value: 'procedureTypeCode_desc', label: '申請種別コード（Z-A）' }
  ]

  return (
    <div className="px-8 py-6">
      {/* Header */}
      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">申請バインド管理</h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: '1rem',
          alignItems: 'end'
        }}>
          {/* キーワード検索 */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>キーワード検索</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="ID、説明、申請種別で検索"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          {/* ステータス */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>ステータス</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <option value="">すべて</option>
              <option value="ACTIVE">有効</option>
              <option value="DRAFT">下書き</option>
              <option value="INACTIVE">無効</option>
              <option value="ARCHIVED">廃止</option>
            </select>
          </div>

          {/* 申請種別 */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>申請種別</label>
            <input
              type="text"
              value={procedureTypeFilter}
              onChange={(e) => setProcedureTypeFilter(e.target.value)}
              placeholder="申請種別コード"
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>
        </div>

        {/* ソート選択と件数表示 */}
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 7L12 3L16 7M8 17L12 21L16 17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              paddingLeft: '0.75rem',
              paddingRight: '2rem',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6'
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            fontWeight: '500'
          }}>
            {totalElements}件のバインド
          </div>
        </div>
      </div>

      {/* 作成ボタン */}
      <div className="mb-4">
        <button
          onClick={() => onNavigate('binding-new')}
          className="btn btn-primary"
        >
          + 新規作成
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">読み込み中...</div>
        ) : bindings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">バインドがありません</div>
        ) : (
          <table className="min-w-full" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>バインドID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ステータス</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>申請種別</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>フォーム</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ワークフロー</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>承認ルート</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>公開日時</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>最終更新</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase" style={{ borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>アクション</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {bindings.map((binding) => (
                <tr key={binding.bindingId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {binding.bindingId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {getStatusBadge(binding.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {binding.procedureName || binding.procedureTypeCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {binding.formName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {binding.workflowName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {binding.approvalRouteName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {binding.publishedAt ? new Date(binding.publishedAt).toLocaleString('ja-JP') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    {new Date(binding.updatedAt).toLocaleString('ja-JP')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => onNavigate('binding-detail', { bindingId: binding.bindingId })}
                      style={{
                        padding: '0.375rem 0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                    >
                      開く
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ページング */}
        {totalPages > 1 && (
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                backgroundColor: page === 0 ? '#f3f4f6' : 'white',
                color: page === 0 ? '#9ca3af' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (page !== 0) e.currentTarget.style.backgroundColor = '#f9fafb'
              }}
              onMouseLeave={(e) => {
                if (page !== 0) e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              前へ
            </button>
            <span style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                backgroundColor: page >= totalPages - 1 ? '#f3f4f6' : 'white',
                color: page >= totalPages - 1 ? '#9ca3af' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (page < totalPages - 1) e.currentTarget.style.backgroundColor = '#f9fafb'
              }}
              onMouseLeave={(e) => {
                if (page < totalPages - 1) e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              次へ
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
